import {openCategoryConfigurator} from './category-configurator.js';
/* Category-neutral shortlist / comparison UI; adapters provide live catalog facts. */
for (const root of document.querySelectorAll("[data-category-selection]"))
  initialize(root);
async function initialize(root) {
  const {
    loadCategory,
    recommend,
    readContext,
    writeContext,
    legacyState,
    addComparison,
    saveProject,
  } = await import(root.dataset.core);
  const category = root.dataset.category,
    mode = root.dataset.mode || "finder",
    label = category === "heating" ? "outdoor heaters" : "fire features",
    status = root.querySelector("[role=status]"),
    grid = root.querySelector("[data-selection-results]");
  const money = (c) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(c / 100);
  const el = (tag, value, cls) => {
    const n = document.createElement(tag);
    if (value) n.textContent = value;
    if (cls) n.className = cls;
    return n;
  };
  const button = (label, fn) => {
    const b = el("button", label, "ho-button ho-button--secondary");
    b.type = "button";
    b.addEventListener("click", fn);
    return b;
  };
  const link = (label, url) => {
    const a = el("a", label);
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    return a;
  };
  let data,
    ranked = [],
    shown = 0;
  const state = () => readContext().shortlists[category] || { compare: [] };
  const persist = (s) => {
    const c = readContext();
    c.shortlists[category] = s;
    return writeContext(c);
  };
  let loading;
  const load = () => data ? Promise.resolve(data) : (loading ||= loadCategory(category, root.dataset.manifest).then(result => {
    const brandSelect = root.querySelector('select[name="brand"]');
    if (brandSelect) {
      const selected = brandSelect.value;
      for (const option of [...brandSelect.options]) if (option.value) option.remove();
      for (const name of [...new Set(result.families.map(family => family.brand).filter(Boolean))].sort()) {
        const option = el('option', name); option.value = name; brandSelect.append(option);
      }
      brandSelect.value = [...brandSelect.options].some(option => option.value === selected) ? selected : '';
    }
    return data = result;
  }).finally(() => loading = null));
  const findVariant = (key) =>
    data.families.flatMap((f) => f.variants).find((v) => v.key === key);
  const notify = (message) => {
    status.textContent = message;
  };
  const download = (name, content) => window.HearthOutpostDocuments.text(name, content);
  function save(v, f) {
    const result = saveProject(category, f, v, {});
    notify(result.message);
    return result.ok;
  }
  function compare(v, b) {
    const result = addComparison(
      category,
      v,
      data.families.flatMap((f) => f.variants),
    );
    notify(result.message);
    if (result.ok) b.textContent = "Added ✓";
  }
  function details(v, f) {
    const d = root.querySelector("dialog");
    d.querySelector("[data-detail-title]").textContent = f.title;
    const body = d.querySelector("[data-detail-body]");
    body.replaceChildren(el("p", `${v.sku} · ${money(v.priceCents)}`));
    const dl = el("dl");
    for (const spec of v.specs) {
      dl.append(el("dt", spec.attribute), el("dd", spec.value));
    }
    body.append(
      dl,
      link("View product & documents", v.url),
      button("Download planning brief", () =>
        download(
          category+"-planning.txt",
          [
            f.title,
            v.sku,
            v.url,
            ...v.specs.map((s) => `${s.attribute}: ${s.value}`),
            "Confirm the exact model manual and installation requirements before ordering.",
          ].join("\n"),
        ),
      ),
    );
    const previous = document.activeElement;
    d.addEventListener("close", () => previous?.focus(), { once: true });
    d.showModal();
  }
  function append(count) {
    const first = shown;
    for (const r of ranked.slice(shown, shown + count)) {
      let v = r.variants[0];
      const a = el("article");
      const t = document.createElement("template");
      t.innerHTML = r.family.card;
      // Expanded catalog snapshots store an image URL instead of rendered HTML.
      // Reuse the native Liquid card template; do not maintain a second card design.
      let card = t.content.querySelector(".ho-product-card");
      if (!card) {
        card = root.querySelector('[data-selection-card-template]')?.content
          .querySelector('.ho-product-card')?.cloneNode(true);
        if (!card) continue;
        card.dataset.familyHandle = r.family.handle;
        card.querySelector('h3').textContent = r.family.title;
        card.querySelector('.ho-home-eyebrow').textContent = r.family.brand;
        const image = document.createElement('img');
        image.width = 800; image.height = 800; image.loading = 'lazy';
        card.querySelector('.ho-product-card-image').replaceChildren(image);
      }
      card.href = v.url;
      const img = card.querySelector("img");
      if (img) {
        img.removeAttribute("srcset");
        img.src = v.image;
        img.alt = r.family.title;
      }
      const price = card.querySelector(".ho-product-price");
      if (price) price.textContent = money(v.priceCents);
      if (r.budgetCeiling)
        a.append(
          el("p", `Up to ${money(r.budgetCeiling * 100)}`, "ho-home-eyebrow"),
        );
      a.append(card);
      const actions = el("div", null, "ho-journey-actions");
      const b = button("Compare", () => compare(v, b));
      actions.append(
        b,
        button(r.variants.length>1?"Configure":"Save to project", () => {if(r.variants.length>1)openCategoryConfigurator({family:r.family,variants:r.variants,selected:v,onSelect:chosen=>{v=chosen;card.href=v.url;if(img&&v.image)img.src=v.image;if(price)price.textContent=money(v.priceCents);b.textContent="Compare";},onSave:chosen=>save(chosen,r.family)});else save(v,r.family);}),
        button("Planning details", () => details(v, r.family)),
      );
      a.append(actions);
      grid.append(a);
    }
    shown = Math.min(shown + count, ranked.length);
    const more = root.querySelector("[data-selection-more]");
    more.hidden = shown >= ranked.length;
    more.textContent = `Show ${Math.min(6, ranked.length - shown)} more`;
    return grid.children[first];
  }
  if (mode === "finder") {
    if(category === "heating") {
      const params=new URLSearchParams(location.search);
      for(const select of root.querySelectorAll("select")) {
        const value=params.get(select.name === "installation"?"placement":select.name);
        if(value && [...select.options].some(o=>o.value===value))select.value=value;
      }
    }
    let generation = 0;
    const find = async () => {
        const request = ++generation;
        root.dataset.filterReady = "false";
        root.querySelector("[data-selection-find]").disabled = true;
        notify("Finding your matches…");
        try {
          await load();
          if(request !== generation) return;
          const answers = Object.fromEntries(
            [...root.querySelectorAll("select")].map((s) => [s.name, s.value]),
          );
          ranked = recommend(data.families, answers);
          shown = 0;
          grid.replaceChildren();
          grid.classList.toggle("ho-journey-results--bands", !answers.budget);
          append(
            answers.budget
              ? 3
              : Math.max(1, ranked.filter((r) => r.featured).length),
          );
          notify(
            ranked.length
              ? ""
              : "No configurations match these choices. Try removing a filter.",
          );
        } catch {
          notify(
            `Product details could not be loaded. Please try again or browse ${label}.`,
          );
        } finally {
          if(request === generation) {root.querySelector("[data-selection-find]").disabled = false;root.dataset.filterReady = "true";}
        }
      };
    root.querySelector("[data-selection-find]").addEventListener("click", find);
    root.querySelectorAll(".ho-category-selects select").forEach(select => select.addEventListener("change", find));
    root.querySelector("[data-selection-find]").hidden = true;
    find();
    root
      .querySelector("[data-selection-more]")
      .addEventListener("click", () => {
        const a = append(6);
        a?.scrollIntoView({
          block: "start",
          behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "instant"
            : "smooth",
        });
      });
    root
      .querySelector("[data-selection-reset]")
      .addEventListener("click", () => {
        root.querySelectorAll("select").forEach((s) => (s.value = ""));
        ranked = [];
        shown = 0;
        grid.replaceChildren();
        root.querySelector("[data-selection-more]").hidden = true;
        notify("");
        find();
      });
    root
      .querySelector("[data-detail-close]")
      .addEventListener("click", () => root.querySelector("dialog").close());
  } else if (mode === "compare") {
    // Reuse the comparison catalog and persisted selection; products can be added here directly.
    const picker = el('div');
    const fields = el('div', null, 'ho-category-selects');
    const searchLabel = el('label', 'Product or model');
    const search = el('input'); search.type = 'search'; search.placeholder = 'Search a product or model';
    searchLabel.append(search);
    const brandLabel = el('label', 'Brand'), brand = el('select');
    const typeLabel = el('label', 'Product type'), type = el('select');
    brandLabel.append(brand); typeLabel.append(type);
    fields.append(searchLabel, brandLabel, typeLabel);
    const results = el('div', null, 'ho-journey-results ho-compare-picker-results');
    const count = el('p'); count.setAttribute('role', 'status');
    let limit = 12;
    let pickerButtons = [];
    const more = button('Show more', () => { limit += 12; showProducts(); });
    picker.append(fields, count, results, more);
    grid.before(picker);
    const option = (label, value) => { const o = el('option', label); o.value = value; return o; };
    function showProducts() {
      const query = search.value.trim().toLowerCase();
      const families = data.families.filter(f => (!brand.value || f.brand === brand.value) &&
        (!type.value || f.variants.some(v => v.fireType === type.value)) &&
        (!query || `${f.brand} ${f.title} ${f.variants.map(v => v.sku).join(' ')}`.toLowerCase().includes(query)));
      count.textContent = `${families.length} matching products`;
      results.replaceChildren();
      pickerButtons = [];
      for (const f of families.slice(0, limit)) {
        const variants = f.variants.filter(v => !type.value || v.fireType === type.value);
        const card = el('article', null, 'ho-compare-picker-card');
        const photo = el('img'); photo.src = variants[0].image; photo.alt = f.title;
        photo.width = 240; photo.height = 180; photo.loading = 'lazy';
        card.append(photo, el('p', f.brand, 'ho-home-eyebrow'), el('h3', f.title));
        const configLabel = el('label', 'Configuration'), configs = el('select');
        for (const v of variants) configs.append(option(`${v.sku} · ${v.variantTitle || v.fuel || ''} · ${money(v.priceCents)}`, v.key));
        configLabel.append(configs); card.append(configLabel);
        const feedback = el('p'); feedback.setAttribute('role', 'status');
        const report = message => { feedback.textContent = message; notify(message); };
        const add = button('Add to comparison', async () => {
          const chosen = variants.find(v => v.key === configs.value);
          add.disabled = true;
          add.setAttribute("aria-busy", "true");
          feedback.textContent = "";
          try {
            if (!chosen) throw Error("Configuration unavailable");
            const current = data.resolveSku ? await data.resolveSku(chosen.sku) : chosen;
            if (!current) throw Error('Configuration unavailable');
            Object.assign(chosen, current);
            const result = addComparison(category, chosen, data.families.flatMap(f => f.variants));
            report(result.message);
            if (result.ok) add.textContent = 'Added ✓';
          } catch { report('This configuration could not be refreshed. Please try again.'); }
          finally { add.disabled = false; add.removeAttribute('aria-busy'); }
        });
        const syncButton = () => {
          const included = state().compare.includes(configs.value);
          if (!included && add.textContent === 'Added ✓') feedback.textContent = '';
          add.textContent = included ? 'Added ✓' : 'Add to comparison';
        };
        pickerButtons.push(syncButton);
        syncButton();
        configs.addEventListener('change', () => { syncButton(); feedback.textContent = ''; photo.src = variants.find(v => v.key === configs.value).image; });
        card.append(add, feedback); results.append(card);
      }
      more.hidden = families.length <= limit;
    }
    if (category === 'fire') {
      load().then(() => {
        brand.append(option('All brands', ''));
        for (const name of [...new Set(data.families.map(f => f.brand))].sort()) brand.append(option(name, name));
        type.append(option('All fire features', ''));
        for (const name of [...new Set(data.families.flatMap(f => f.variants.map(v => v.fireType)))].filter(Boolean).sort()) type.append(option(name, name));
        // Suggest the same product type accepted by the existing comparison rules.
        const selectedType = state().compare.map(findVariant).find(Boolean)?.fireType;
        if (selectedType && [...type.options].some(o => o.value === selectedType)) type.value = selectedType;
        for (const input of [search, brand, type]) input.addEventListener(input === search ? 'input' : 'change', () => { limit = 12; showProducts(); });
        showProducts();
      }).catch(() => { count.textContent = 'Products could not be loaded. Please refresh to try again.'; });
    } else picker.remove();
    async function render() {
      notify("Loading your comparison…");
      try {
        await load();
        grid.replaceChildren();
        const selected = state().compare.map(findVariant).filter(Boolean);
        for (const syncButton of pickerButtons) syncButton();
        // Keep existing selections above the catalog; retain the empty-state picker.
        if (category === "fire") {
          if (selected.length) grid.after(picker);
          else grid.before(picker);
        }
        if (!selected.length) {
          notify(
            category === "fire" ? "Choose products above to add to your comparison." : `Add ${label} from the category shortlist to compare them here.`,
          );
          return;
        }
        notify("Swipe or scroll sideways to compare all selections.");
        const wrap = el("div", null, "ho-category-comparison-scroll");
        wrap.tabIndex = 0;
        wrap.setAttribute("role", "region");
        wrap.setAttribute(
          "aria-label",
          `${label} comparison, scroll horizontally`,
        );
        const table = el("table");
        table.style.setProperty("--comparison-columns", selected.length + 1);
        const head = el("tr");
        head.append(el("th", "Compare the details"));
        selected.forEach((v) => {
          const f = data.families.find((f) =>
            f.variants.some((x) => x.key === v.key),
          );
          const th = el("th");
          const logos = JSON.parse(root.querySelector("[data-comparison-brand-logos]")?.textContent || "{}");
          const brand = f.brand || v.brand;
          if (logos[brand]) {
            const logo = el("img", null, "ho-category-comparison-logo");
            logo.src = logos[brand]; logo.alt = brand; logo.width = 130; logo.height = 45;
            logo.addEventListener("error", () => logo.replaceWith(el("p", brand)), {once:true});
            th.append(logo);
          } else if (brand) th.append(el("p", brand));
          const photo = el("img", null, "ho-category-comparison-image");
          photo.src = v.image; photo.alt = f.title; photo.width = 240; photo.height = 240;
          th.append(photo);
          th.append(
            link(f.title, v.url),
            el("p", v.sku),
            button("Remove", () => {
              const s = state();
              s.compare = s.compare.filter((k) => k !== v.key);
              persist(s);
              render();
            }),
          );
          head.append(th);
        });
        table.append(head);
        const comparisonRows = category === "heating" ? [
 ["Price",v=>money(v.priceCents)], ["Availability",v=>v.available?"In Stock":"Out of Stock"], ["Placement",v=>v.installation], ["Fuel",v=>v.fuel], ["Heat output",v=>v.options.Power||"See product documents"], ["Electrical supply",v=>v.options.Supply||"See product documents"], ["Color",v=>v.options.Color||"See product documents"], ["Included mounting",v=>v.specs.filter(s=>s.attribute==="Included mounting").map(s=>s.value).join(" · ")||"See product documents"], ["Controls",v=>v.specs.find(s=>s.attribute==="Controls")?.value||"See product documents"]
 ] : [
          ["Price", (v) => money(v.priceCents)],
          ["Availability", (v) => v.available ? "In Stock" : "Out of Stock"],
          ["Type", (v) => v.heatingType || v.fireType],
          ["Heat output",v=>v.specs.filter(s=>/burner output|heat output|btu/i.test(s.attribute)).map(s=>s.value).join(' · ')],
          ["Weight",v=>v.specs.filter(s=>/weight/i.test(s.attribute)).map(s=>s.value).join(' · ')],
          ["Propane storage",v=>v.specs.find(s=>/propane storage/i.test(s.attribute))?.value],
          ["Warranty",v=>v.specs.filter(s=>s.attribute==='Warranty').map(s=>s.value).join(' · ')],
          ["Fuel", (v) => v.fuel || "—"],
          ["Shape", (v) => v.shape || "—"],
          [
            "Dimensions",
            (v) =>
              v.specs
                .filter((s) =>
                  /size|width|length|height|depth|diameter/i.test(s.attribute) && !(s.attribute === "Size" && v.specs.some(x=>/^(width|length|height|depth|diameter)$/i.test(x.attribute))),
                )
                .map((s) => s.attribute + ": " + s.value)
                .join(" · ") || "—",
          ],
          [
            "Ignition",
            (v) =>
              v.options.Ignition ||
              v.options["Ignition System"] ||
              "—",
          ],
          [
            "Material / finish",
            (v) =>
              Object.entries(v.options)
                .filter(([k]) => /material|finish|color|^top$/i.test(k))
                .map(([k, v]) => k + ": " + v)
                .join(" · ") || "—",
          ],
          [
            "Included",
            (v) =>
              v.specs
                .filter((s) => s.attribute === "Included")
                .map((s) => s.value)
                .join(" · ") || "—",
          ],
        ];
        for (const [name, get] of comparisonRows) {
          if(category === "fire" && selected.every(v=>!get(v)||get(v)==="—"))continue;
          const tr = el("tr");
          const th = el("th", name);
          th.scope = "row";
          tr.append(th);
          selected.forEach((v) => tr.append(el("td", get(v)||"—")));
          table.append(tr);
        }
        wrap.append(table);
        grid.append(wrap);
      } catch {
        notify("Comparison could not be loaded. Please try again.");
      }
    }
    render();
    window.addEventListener("storage",()=>render());
    window.addEventListener("ho-category-context",()=>render());
  } else if (mode === "projects") {
    const render = () => {
      const s = legacyState(),
        saved =
          readContext().projects[s.activeProjectId]?.categories?.[category]
            ?.saved || [];
      root.hidden = !saved.length;
      grid.replaceChildren();
      for (const item of saved) {
        const row = el("div", null, "ho-journey-toolbar");
        row.append(
          link(item.title + " · " + item.sku, item.url),
          button("Remove", () => {
            const c = readContext();
            c.projects[s.activeProjectId].categories[category].saved =
              saved.filter((i) => i.key !== item.key);
            writeContext(c);
            render();
          }),
        );
        grid.append(row);
      }
    };
    render();
    window.addEventListener("napoleon-saved", render);
    window.addEventListener("ho-category-context", render);
  }
  if (mode === "finder")
    root.querySelector("[data-selection-find]").disabled = false;
  root.dataset.ready = "true";
}
